import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import GoogleReviews from '@/components/GoogleReviews';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, Phone, FileText, Clock, Users, Shield, Star, ArrowRight, Calendar } from 'lucide-react';
import { openCalendlyPopup } from '@/utils/calendly';
import WhatsAppButton from '@/components/WhatsAppButton';
const servicePackages = [{
  title: "Basis Service",
  price: "€ 12,50",
  period: "per maand",
  description: "Voor wie basisondersteuning wil bij verzekeringszaken",
  features: ["Jaarlijkse polis controle", "Telefonische ondersteuning", "Schadeondersteuning", "Poliswijzigingen"],
  popular: false
}, {
  title: "Compleet Service",
  price: "€ 19,50",
  period: "per maand",
  description: "Onze meest gekozen service voor complete ontzorging",
  features: ["Alles van Basis Service", "Proactieve adviesgesprekken", "Markt monitoring", "Preventie advies", "Prioriteit bij afspraken", "Digitaal dossier beheer"],
  popular: true
}, {
  title: "Premium Service",
  price: "€ 29,50",
  period: "per maand",
  description: "Voor wie de allerbeste service en persoonlijke aandacht wil",
  features: ["Alles van Compleet Service", "Dedicated account manager", "24/7 noodservice", "Jaarlijks adviesgesprek", "Gratis tweede opinies", "VIP behandeling", "Exclusieve events"],
  popular: false
}];
const serviceVoordelen = [{
  icon: <Clock className="w-8 h-8 text-primary" />,
  title: "Tijdsbesparing",
  description: "Wij regelen alles voor u. Geen gedoe met verschillende verzekeraars meer."
}, {
  icon: <Shield className="w-8 h-8 text-primary" />,
  title: "Optimale dekking",
  description: "Uw verzekeringen blijven altijd up-to-date door onze proactieve monitoring."
}, {
  icon: <Phone className="w-8 h-8 text-primary" />,
  title: "Directe hulp",
  description: "Bij schades of vragen belt u direct met ons. Wij regelen de rest."
}, {
  icon: <FileText className="w-8 h-8 text-primary" />,
  title: "Administratie",
  description: "Alle polissen en documenten digitaal op één plek georganiseerd."
}];
const Serviceabonnement = () => {
  const scrollToPackages = () => {
    const element = document.getElementById('pakketten');
    element?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen">
      <Helmet>
        <title>Serviceabonnement | Complete verzekeringsontzorging | Compleet Verzekerd</title>
        <meta name="description" content="Serviceabonnement voor complete verzekeringsontzorging. Geen provisie maar transparante maandprijs. Persoonlijke service en optimale dekking gegarandeerd." />
        <meta name="keywords" content="serviceabonnement, verzekeringsontzorging, geen provisie, transparante prijzen, persoonlijke service, compleet verzekerd" />
        <link rel="canonical" href="https://compleetverzekerd.nl/serviceabonnement/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Serviceabonnement Verzekeringen",
            "provider": {
              "@type": "Organization",
              "name": "Compleet Verzekerd",
              "telephone": "0782200021"
            },
            "description": "Complete verzekeringsontzorging via serviceabonnement zonder provisie",
            "serviceType": "Insurance Service Subscription"
          })}
        </script>
      </Helmet>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded">
        Spring naar hoofdinhoud
      </a>
      
      <Header />
      
      <main id="main-content">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary/10 to-accent/10 py-16 md:py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
                Serviceabonnement
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Complete ontzorging op het gebied van verzekeringen. Voor één vaste prijs per maand 
                regelen wij alles voor u. Van schadeafhandeling tot proactief advies.
              </p>
              <Button size="lg" onClick={openCalendlyPopup} className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3">
                <Calendar className="h-5 w-5" />
                Plan een afspraak
              </Button>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Waarom een serviceabonnement?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Meer dan alleen verzekeringen afsluiten. Wij zorgen ervoor dat u altijd optimaal verzekerd bent.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {serviceVoordelen.map((voordeel, index) => <Card key={index} className="text-center border-border hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="mb-4 flex justify-center">{voordeel.icon}</div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">{voordeel.title}</h3>
                    <p className="text-muted-foreground">{voordeel.description}</p>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </section>

        {/* Explanation Section */}
        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Een serviceabonnement? Natuurlijk!</h2>
              </div>
              
              <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
                <p>
                  In tegenstelling tot veel andere verzekeraars en banken werken wij met een serviceabonnement. 
                  Je betaalt bij ons geen provisie, en dat scheelt in de kosten. Provisie wordt namelijk vaak 
                  verwerkt in de verzekeringspremie. En die premie, die betaal jij.
                </p>
                
                <p>
                  Met een serviceabonnement zijn de kosten super transparant. Je weet altijd precies wat je 
                  ons betaalt, en voor welke dienstverlening je ons betaalt. En je ontvangt een flinke korting 
                  op je verzekeringspremies!
                </p>
                
                <p>
                  Je voordeel wordt nog groter als je meer verzekeringen via Compleet Verzekerd regelt. 
                  De provisie op verzekeringen kan namelijk hoog zijn in verhouding tot de werkzaamheden. 
                  Met een serviceabonnement ben je dus goedkoper uit.
                </p>
                
                <div className="text-center bg-primary/10 rounded-lg p-6 mt-8">
                  <p className="text-xl font-semibold text-primary">
                    Al je verzekeringen onder één dak. Persoonlijke service. Goed geregeld.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section - TEMPORARILY COMMENTED OUT */}
        {/*
         <section id="pakketten" className="py-16 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Kies uw servicepakket
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Transparante prijzen, geen verrassingen. Opzegbaar per maand.
              </p>
            </div>
            
            <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {servicePackages.map((pkg, index) => (
                <Card 
                  key={index} 
                  className={`relative border-border hover:shadow-lg transition-shadow ${
                    pkg.popular ? 'ring-2 ring-primary scale-105' : ''
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium">
                        Meest gekozen
                      </div>
                    </div>
                  )}
                  
                  <CardHeader className="text-center pb-4">
                    <CardTitle className="text-2xl font-bold text-foreground">{pkg.title}</CardTitle>
                    <div className="mt-4">
                      <span className="text-4xl font-bold text-foreground">{pkg.price}</span>
                      <span className="text-muted-foreground ml-2">{pkg.period}</span>
                    </div>
                    <p className="text-muted-foreground mt-2">{pkg.description}</p>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <ul className="space-y-3 mb-6">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center">
                          <Check className="w-5 h-5 text-primary mr-3 flex-shrink-0" />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Button 
                      className="w-full" 
                      variant={pkg.popular ? "default" : "outline"}
                    >
                      Kies dit pakket
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
         </section>
         */}

        {/* Contact Section */}
        <section id="pakketten" className="py-16 bg-muted/50">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Serviceabonnement op maat
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Wij vertellen u graag tijdens een persoonlijk gesprek hoe ons serviceabonnement werkt 
                en wat de kosten zijn. Elke situatie is anders, daarom bieden wij maatwerk.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="default" size="lg" asChild>
                  <a href="tel:0782200021">
                    <Phone className="h-4 w-4" />
                    078 - 220 00 21
                  </a>
                </Button>
                <Button variant="outline" size="lg" onClick={openCalendlyPopup}>
                  <Calendar className="h-5 w-5" />
                  Plan een afspraak
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Google Reviews & Advieskeuze Reviews */}
        <GoogleReviews />

        {/* CTA Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-6 text-center">
            <Users className="w-16 h-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Klaar voor complete ontzorging?
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Start vandaag nog met uw serviceabonnement en ervaar het gemak van professionele verzekeringshulp.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="secondary" size="lg" className="bg-background text-foreground hover:bg-background/90" onClick={openCalendlyPopup}>
                <Calendar className="h-5 w-5" />
                Plan een afspraak
              </Button>
              <Button variant="outline-inverse" size="lg" asChild>
                <a href="tel:0782200021">
                  <Phone className="h-5 w-5" />
                  078 - 220 00 21
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <WhatsAppButton />
    </div>;
};
export default Serviceabonnement;